import { useQuery } from '@tanstack/react-query'

import type { GrupoDelEsquema } from '../motor/esquema'
import { consulta, pedir } from './nucleo'
import type { Lista } from './tipos'

/**
 * La estructura de una web (plan del CMS dinámico §2 y §7): sus tipos de contenido, sus
 * taxonomías y lo que se puede vincular en una plantilla. Se lee por la REST de siempre, con
 * los permisos de quien mira: con `estructura.ver` llegan también los borradores; con solo
 * `contenido.ver`, lo publicado y activo. El panel no filtra nada que el núcleo no filtre.
 */
export type TipoDeContenido = {
  id: number
  nombre: string
  nombrePlural: string
  slug?: null | string
  icono?: null | string
  descripcion?: null | string
  conArchivo?: boolean | null
  conFicha?: boolean | null
  jerarquico?: boolean | null
  camposBase?: null | string[]
  taxonomias?: null | Array<number | { id: number }>
  estado?: 'activo' | 'archivado' | null
  posicion?: null | number
  _status?: 'draft' | 'published' | null
}

export type Taxonomia = { id: number; nombre: string; nombrePlural: string; slug?: null | string; jerarquica?: boolean | null; _status?: 'draft' | 'published' | null }

export type Termino = { id: number; nombre: string; slug?: null | string; padre?: null | number | { id: number; nombre?: string }; taxonomia: number | { id: number } }

const idDe = (v: number | { id: number }): number => (typeof v === 'number' ? v : v.id)

/** Los ids de las taxonomías de un tipo, lleguen pobladas o no. */
export const taxonomiasDelTipo = (tipo: Pick<TipoDeContenido, 'taxonomias'> | null | undefined): number[] => (tipo?.taxonomias ?? []).map(idDe)

/**
 * Los tipos activos de una web, por su posición: una entrada del menú por cada uno. Con los
 * borradores (quien ve la estructura puede ir creando contenidos antes de publicar el tipo).
 * La clave cuelga de «lista» de la colección: se olvida al guardar un tipo.
 */
export const useTiposDeContenido = (sitio: null | number, activo = true) =>
  useQuery({
    queryKey: ['lista', 'tipos-de-contenido', 'activos', sitio],
    enabled: activo && sitio !== null,
    staleTime: 60_000,
    queryFn: async () =>
      (
        await pedir<Lista<TipoDeContenido>>(
          `/tipos-de-contenido${consulta({ 'where[sitio][equals]': sitio ?? undefined, 'where[estado][not_equals]': 'archivado', limit: 100, depth: 0, sort: 'posicion', draft: true })}`,
        )
      ).docs,
  })

/** Un tipo de contenido (su última versión, borrador o no). */
export const useTipoDeContenido = (id: null | number) =>
  useQuery({
    queryKey: ['doc', 'tipos-de-contenido', id],
    enabled: id !== null,
    queryFn: () => pedir<TipoDeContenido>(`/tipos-de-contenido/${id}${consulta({ depth: 0, draft: true })}`),
  })

/** Una taxonomía (su última versión). */
export const useTaxonomia = (id: null | number) =>
  useQuery({
    queryKey: ['doc', 'taxonomias', id],
    enabled: id !== null,
    queryFn: () => pedir<Taxonomia>(`/taxonomias/${id}${consulta({ depth: 0, draft: true })}`),
  })

/** Varias taxonomías por id, en una petición (las de un tipo). */
export const useTaxonomias = (ids: number[]) =>
  useQuery({
    queryKey: ['lista', 'taxonomias', 'por-id', ids.join(',')],
    enabled: ids.length > 0,
    queryFn: async () => (await pedir<Lista<Taxonomia>>(`/taxonomias${consulta({ 'where[id][in]': ids.join(','), limit: ids.length, depth: 0, sort: 'posicion', draft: true })}`)).docs,
  })

/** Los términos de una taxonomía, por su posición (hasta 500: una taxonomía con más se elige buscando). */
export const useTerminosDe = (taxonomia: number, sitio: null | number) =>
  useQuery({
    queryKey: ['lista', 'terminos', 'de', taxonomia, sitio],
    enabled: sitio !== null,
    queryFn: async () =>
      (await pedir<Lista<Termino>>(`/terminos${consulta({ 'where[taxonomia][equals]': taxonomia, 'where[sitio][equals]': sitio ?? undefined, limit: 500, depth: 0, sort: 'posicion' })}`)).docs,
  })

/**
 * Los grupos de campos que aplican a un documento **todavía sin guardar** (un contenido nuevo
 * de un tipo, un término nuevo de una taxonomía): lo evalúa el núcleo (`POST /api/panel/grupos`)
 * con lo que ya lleva puesto. Para uno guardado, los trae el esquema con su id.
 */
export const useGruposDeUnNuevo = (coleccion: string, sitio: null | number, documento: Record<string, unknown>, activo = true) =>
  useQuery({
    queryKey: ['grupos', coleccion, sitio, JSON.stringify(documento)],
    enabled: activo && sitio !== null,
    staleTime: 60_000,
    queryFn: () => pedir<{ grupos: GrupoDelEsquema[] }>('/panel/grupos', { metodo: 'POST', cuerpo: { coleccion, sitio, documento } }),
  })

// --- «Vincular a…» --------------------------------------------------------------------------

/** La clase de un campo de bloque, en la forma de `@mc/vinculos` (`text`, `upload`, `array`, `enlace`, `mapa`…). */
export type ClaseDeCampo = string

export type Vinculable = { expresion: string; tipoDeValor: string; etiqueta: string; grupo: string; cabeEn: ClaseDeCampo[] }

export type Vinculables = {
  contexto: 'archivo' | 'ficha' | 'termino'
  vinculables: Vinculable[]
  /** Por bloque, la clase de cada campo con la ruta sin índices (`botones.etiqueta`). Incluye los destinos que no son campos de Payload. */
  bloques: Record<string, { campos: Record<string, ClaseDeCampo> }>
}

/** De qué plantilla se pregunta: la ficha o el archivo de un tipo, o la de los términos de una taxonomía. */
export type OrigenDeVinculos = { tipo: number; contexto: 'archivo' | 'ficha' } | { taxonomia: number }

export const useVinculables = (origen: null | OrigenDeVinculos) =>
  useQuery({
    queryKey: ['vinculables', origen],
    enabled: origen !== null,
    staleTime: 30_000,
    queryFn: () =>
      pedir<Vinculables>(
        `/panel/estructura/vinculables${consulta(origen && 'taxonomia' in origen ? { taxonomia: origen.taxonomia } : origen ? { tipo: origen.tipo, contexto: origen.contexto } : {})}`,
      ),
  })
